// A ticket handed to the player by the dedicated dashboard (P-40, operator test sessions).
//
// THE FLOW (dedicated edition only):
//   1. A signed-in platform administrator presses "Test on this host" on the
//      dashboard (Capacity → GPU hosts). The dashboard creates the pin
//      (POST /admin/ops/hosts/:hostname/pin-session) and redeems it ITSELF with
//      its login token (POST /stream/ticket { projectId, pinId }): on the
//      dedicated edition platform-api accepts a pin only from the signed-in
//      administrator who created it, and only once (401 PIN_SIGN_IN_REQUIRED,
//      403 PIN_NOT_YOURS, 409 PIN_USED). The player has no sign-in, so it never
//      sees the pin.
//   2. The dashboard opens the player at <share>/<projectId>#admission=<handover>,
//      where <handover> is base64url(JSON) of the ticket response it got:
//      { v: 1, projectId, ticket, sessionId, telemetryToken, voiceToken?, config }.
//      A FRAGMENT, never a query parameter: browsers do not send the fragment
//      to any server, so the ticket is in no request line, access log or
//      Referer. The whole response travels, not just the ticket: the player
//      also needs the stream config, the pinned session id (the scheduler's
//      key, which goes on the signalling URL) and the telemetry/chat tokens,
//      and it cannot ask for them without minting again.
//   3. The player reads the fragment when its bundle loads, removes it from the
//      address bar at once (history.replaceState — the current history entry is
//      rewritten, so neither the address bar nor Back shows it), skips its own
//      /stream/ticket call and uses the handed ticket for first-message
//      admission ({"type":"admission","ticket"} — never in the signalling URL,
//      whatever SPX_TICKET_IN_URL says; this needs the P-39 signalling).
//   4. One test, one session: a Retry after that session ends does not mint
//      an ordinary session in its place — the card says to start a new test
//      from the dashboard (the pin is spent anyway).
//
// A forged fragment gains nothing: its ticket is whatever its author minted,
// and the page's CSP keeps the player's connections on the platform's own hosts.
//
// CommonJS and dependency-free so plain node can test it (test/admission-handover.js).

const HANDOVER_PARAM = 'admission';
const HANDOVER_VERSION = 1;
const JWT_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const SESSION_ID_RE = /^[A-Za-z0-9_-]{8,128}$/;
const PROJECT_ID_RE = /^[a-f0-9]{24}$/i;
const HOST_RE = /^[A-Za-z0-9.-]{1,253}(:\d{1,5})?$/;
const MAX_FRAGMENT = 64 * 1024;

function base64UrlEncode(text) {
  const bytes = new TextEncoder().encode(text);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(b64url) {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

// What the dashboard puts after #admission= (the dashboard has its own copy in
// src/lib/testSession.ts; this one documents the format and serves the tests).
function encodeHandover({ projectId, ticket, sessionId, telemetryToken, voiceToken, config }) {
  // No object spread in this file: Babel would import a helper for it, which turns the
  // module into an ES module and hides these CommonJS exports from App.js.
  const out = { v: HANDOVER_VERSION, projectId, ticket, sessionId, telemetryToken };
  if (voiceToken) out.voiceToken = voiceToken;
  out.config = config;
  return base64UrlEncode(JSON.stringify(out));
}

// The fragment's other parameters, if any, without the handover.
function hashWithout(hash) {
  const params = new URLSearchParams(String(hash || '').replace(/^#/, ''));
  params.delete(HANDOVER_PARAM);
  const rest = params.toString();
  return rest ? `#${rest}` : '';
}

// `hash`: window.location.hash. Returns
//   null                        — no handover in the fragment
//   { error }                   — one was there but is unusable (malformed, wrong version, missing fields)
//   { projectId, ticket, sessionId, telemetryToken, voiceToken, config }
function readHandover(hash) {
  const raw = String(hash || '');
  if (!raw.includes(`${HANDOVER_PARAM}=`)) return null;
  const value = new URLSearchParams(raw.replace(/^#/, '')).get(HANDOVER_PARAM);
  if (!value) return null;
  if (value.length > MAX_FRAGMENT) return { error: 'too-large' };
  let data;
  try { data = JSON.parse(base64UrlDecode(value)); } catch (e) { return { error: 'malformed' }; }
  if (!data || typeof data !== 'object' || data.v !== HANDOVER_VERSION) return { error: 'version' };
  if (typeof data.ticket !== 'string' || !JWT_RE.test(data.ticket)) return { error: 'ticket' };
  if (typeof data.projectId !== 'string' || !PROJECT_ID_RE.test(data.projectId)) return { error: 'project' };
  if (typeof data.sessionId !== 'string' || !SESSION_ID_RE.test(data.sessionId)) return { error: 'session' };
  const config = data.config;
  if (!config || typeof config !== 'object' || Array.isArray(config)) return { error: 'config' };
  if (typeof config.signallingHost !== 'string' || !HOST_RE.test(config.signallingHost)) return { error: 'config' };
  return {
    projectId: data.projectId,
    ticket: data.ticket,
    sessionId: data.sessionId,
    telemetryToken: typeof data.telemetryToken === 'string' ? data.telemetryToken : undefined,
    voiceToken: typeof data.voiceToken === 'string' ? data.voiceToken : undefined,
    config,
  };
}

// Reads the handover (if any) and takes it out of the address bar straight away,
// keeping the path, the query and any other fragment parameters.
function takeHandover(loc, hist) {
  if (!loc) return null;
  const found = readHandover(loc.hash);
  if (found && hist && typeof hist.replaceState === 'function') {
    try {
      hist.replaceState(hist.state, '', `${loc.pathname}${loc.search}${hashWithout(loc.hash)}`);
    } catch (e) { /* a sandboxed frame may refuse; the fragment never reached a server either way */ }
  }
  return found;
}

// The viewer-facing line for a pinned link the platform refused (an old
// share/<id>?pin=<pinId> link on the dedicated edition, where the player cannot
// redeem a pin itself). null for anything that is not a pin refusal.
function pinRefusalMessage(code) {
  switch (code) {
    case 'PIN_SIGN_IN_REQUIRED':
    case 'PIN_NOT_YOURS':
      return 'Test sessions on this platform are started from the dashboard by the administrator who created them.';
    case 'PIN_USED':
      return 'This test session has already been used. Start a new one from the dashboard.';
    case 'PIN_EXPIRED':
    case 'PIN_INVALID':
      return 'This test link has expired. Start a new test from the dashboard.';
    default:
      return null;
  }
}

module.exports = {
  HANDOVER_PARAM,
  HANDOVER_VERSION,
  encodeHandover,
  readHandover,
  takeHandover,
  hashWithout,
  pinRefusalMessage,
};
